import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Clear } from 'basic-components/icons';
import { StatusesEnums } from 'dashboard-services/api/statuses';
import TimeService from 'dashboard-services/services/TimeService';
import { StatusIcon } from 'primary-components';

import { routing } from 'routing';

import NoStatuses from './NoStatuses';
import StatusDetails from './StatusDetails';

import { Else, If, Then, When } from 'react-if';
import { createSelector } from 'reselect';

import classNames from 'classnames';
import './StatusBox.scss';

const getStatuses = state => state.datascienceState.creatorState.basicState.statuses
const getComputedStatuses = createSelector([getStatuses, (_, nodeId) => nodeId], (allStatuses, nodeId) => {
  const thisStatus = allStatuses.find(s => s.payload?.nodeId === nodeId)
  const results = []
  if(!thisStatus) {
    return results;
  }
  const queuedEIds = [thisStatus.eId]
  const visitedEIds = []

  while(queuedEIds.length > 0) {
    const currentEId = queuedEIds.shift()

    if(!visitedEIds.includes(currentEId)) {
      visitedEIds.push(currentEId)
      const newStatuses = allStatuses.filter(status => status.prevEId === currentEId)
      results.push(...newStatuses)
      queuedEIds.push(...newStatuses.map(s => s.eId))
    }
  }
  return results
})

const StatusBox = ({ close, nodeId, taskName, defaultStatuses, startTime = undefined }) => {
  const statuses = useSelector(state => defaultStatuses ? defaultStatuses : getComputedStatuses(state, nodeId)),
        definedTime = useSelector(state => state.myAccountState.settingsState.definedTime),
        [closeAnimation, setCloseAnimation] = useState(false),
        timeoutRef = useRef(),
        onClose = useCallback(() => {
          setCloseAnimation(true)
          clearTimeout(timeoutRef.current)
          timeoutRef.current = setTimeout(() => {
            setCloseAnimation(false)
            close()
          }, 201)
        }, [close]),
        nodeRef = useRef(null),
        onScroll = useCallback(e => {
          e.stopPropagation()
          e.preventDefault()
        }, [])

  useEffect(() => () => {
    clearTimeout(timeoutRef.current)
  }, [])
  return (
    <div 
        className={classNames(
          "data-science__pipeline-creator__body__workspace__diagram__status-box",
          { "data-science__pipeline-creator__body__workspace__diagram__status-box--is-closing": closeAnimation }
        )}
        onScroll={onScroll}
        ref={nodeRef}
    >
      <p className="data-science__pipeline-creator__body__workspace__diagram__status-box__name"> 
        {taskName + " Statuses:"}
      </p>
      <div 
          className="data-science__pipeline-creator__body__workspace__diagram__status-box__close"
          onClick={onClose}
      >
        <Clear
            color="black-base"
            height={16}
            width={16}
        />
      </div>
      <When condition={startTime}>
        {() => (
          <div className="data-science__pipeline-creator__body__workspace__diagram__status-box__start-time">
            {TimeService.getUserFormattedDateTime(startTime, definedTime)}
          </div>
        )}
      </When>
      <If condition={(statuses || []).length === 0}>
        <Then>
          {() => (
            <NoStatuses/>
          )}
        </Then>
        <Else>
          {() => (
            <div className="data-science__pipeline-creator__body__workspace__diagram__status-box__container">
              {(statuses || []).map(({ eId, status, startTime, endTime, message, component, payload, warnStatuses }) => (
                <div 
                    className="data-science__pipeline-creator__body__workspace__diagram__status-box__container__row"
                    key={`${eId}`}
                >
                  <div className="data-science__pipeline-creator__body__workspace__diagram__status-box__container__row__icon">
                    <StatusIcon
                        status={status}
                        warnStatuses={warnStatuses}
                    />
                  </div>
                  <div className="data-science__pipeline-creator__body__workspace__diagram__status-box__container__row__component">
                    {component}
                  </div>
                  <div className="data-science__pipeline-creator__body__workspace__diagram__status-box__container__row__message">
                    {message}
                  </div>
                  <div className="data-science__pipeline-creator__body__workspace__diagram__status-box__container__row__time">
                    {TimeService.getIsoDurationBetween(startTime, endTime, status === StatusesEnums.STATUS.RUNNING || status === StatusesEnums.STATUS.PAUSED)}
                  </div>
                  <When condition={component === routing.dataLake.name}>
                    {() => (
                      <StatusDetails 
                          component={component}  
                          fileName={payload.fileName} 
                          fileType={payload.fileType}
                          group={payload?.groupName || payload?.group}/>
                        )}
                  </When>
                </div>
              ))}
            </div>
          )}
        </Else>
      </If>
    </div>
  )
}

StatusBox.propTypes = {
  close: PropTypes.func.isRequired,
  defaultStatuses: PropTypes.array,
  nodeId: PropTypes.string.isRequired,
  startTime: PropTypes.string,
  taskName: PropTypes.string.isRequired
}

export default memo(StatusBox);
